import NotificationsPanelActions from "PFApp/notifications/notifications_panel_actions";
import { NotificationsPanelFilters } from "PFApp/notifications/notifications_panel_filters";
import { NotificationsPanelList } from "PFApp/notifications/notifications_panel_list";
import { useAppLayoutStoreActions } from "PFApp/stores/app_layout_store";
import { ActionIcon } from "PFComponents/action_icon";
import { Stack } from "PFComponents/containers";
import { Typography } from "PFComponents/typography/typography";
import { Inline } from "PFCore/components/containers/flex/inline";
import { NotificationGroupType } from "PFCore/types";
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { NOTIFICATIONS_PANEL_ID } from "./constants";
import { useNotificationsPanelContext } from "./contexts/notifications_panel_context";
import { NotificationsPanelContextProvider } from "./contexts/notifications_panel_context_provider";
import { useStickyControls } from "./hooks/use_sticky_controls";
import css from "./notifications_panel.module.scss";
import { NotificationsPanelGroups } from "./notifications_panel_groups";

const NotificationsPanelContent = () => {
  const { t } = useTranslation("translation", { keyPrefix: "notifications" });
  const { categories, engagement, role, showUnreadOnly, groupBy } = useNotificationsPanelContext();
  const [filtersDisabled, setFiltersDisabled] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const shadowRef = useRef<HTMLDivElement>(null);
  const { handleScroll, handleAccordionStateChange } = useStickyControls({
    headerRef,
    scrollableContainerRef,
    filtersRef,
    shadowRef,
    onFiltersVisibilityChange: (visible) => setFiltersDisabled(!visible)
  });
  const { setLeftActionPanel } = useAppLayoutStoreActions();

  useEffect(() => {
    if (!scrollableContainerRef.current) {
      return;
    }
    scrollableContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }, [engagement, role, categories, showUnreadOnly]);

  const onClose = (e: SyntheticEvent) => {
    e.preventDefault();
    setLeftActionPanel({ id: NOTIFICATIONS_PANEL_ID, collapsed: true });
  };

  return (
    <Stack gap="spacingLg" className={css.wrapper}>
      <Inline spread>
        <Typography variant="h4" qaId="LeftActionPanel.title">
          {t("title")}
        </Typography>
        <ActionIcon size="sm" name="cross" onClick={onClose} disableHover />
      </Inline>
      <div ref={scrollableContainerRef} className={css.scrollableContainer} onScroll={handleScroll}>
        <div ref={headerRef} className={css.controls}>
          <NotificationsPanelFilters
            ref={filtersRef}
            disabled={filtersDisabled}
            onAccordionStateChange={handleAccordionStateChange}
          />
          <NotificationsPanelActions />
          <div ref={shadowRef} className={css.shadow} />
        </div>
        {groupBy !== NotificationGroupType.None ? <NotificationsPanelGroups /> : <NotificationsPanelList />}
      </div>
    </Stack>
  );
};

export const NotificationsPanel = () => (
  <NotificationsPanelContextProvider>
    <NotificationsPanelContent />
  </NotificationsPanelContextProvider>
);
